import {
  useQuery,
  useMutation,
  UseQueryOptions,
  UseMutationOptions,
} from 'react-query';
import {
  getCompanyById,
  getCompanyListByUserId,
  getCompanyCoverLetter,
  getCompanySetupState,
  getOwner,
  getOwners,
  initIncorporationStatus,
  notifyIncorporationStatus,
  setCompanySetupState,
  updateCompanyById,
  updateCompanyCoverLetter,
  updateIncorporationStatus,
  activateBusiness,
  createOwner,
  updateOwner,
  deleteOwner,
  createCompany,
  updateCompanyProgressById,
  updateBusinessGoals,
  getBusinessProjectedGrossIncome,
  getProjectedBusinessExpense,
} from 'services/companies';
import { FormationsOrganization } from 'models/api';
import { Business } from 'models/business';
import { omit } from 'lodash';
import {
  BusinessGoalsRequest,
  BusinessProjectedGrossIncome,
  BusinessProjectedBusinessExpense,
  CompanySetupState,
  OwnerRequestData,
  Owner,
  UpdateCompanySetupState,
  UpdateIncorporationStatusSetting,
} from 'models/company';
import { useFormationsAuth } from 'auth/FormationsAuthProvider';
import { ProgressTrackerGroupRequest } from 'models/account';
import { queryClient } from 'states/reactQueryClient';
import { useAuth0 } from '@auth0/auth0-react';
import { AuthService } from 'services';
import { useCurrentAccount } from './useAccounts';

export const useCurrentCompanyList = (
  currentUserId: string | undefined,
  queryOptions: UseQueryOptions<Business[]> = {},
) => {
  const token = AuthService.userToken();
  const { data, ...rest } = useQuery<Business[]>(
    ['currentCompanyList', currentUserId],
    () => getCompanyListByUserId(currentUserId!),
    {
      ...queryOptions,
      enabled: !!currentUserId && !!token,
    },
  );
  return {
    companies: data || [],
    ...rest,
  };
};

export const useDefaultCompanyForAccount = (
  currentUserId: string | undefined,
) => {
  const { companies: currentCompanies } = useCurrentCompanyList(currentUserId);
  return currentCompanies[0]?.id;
};

export const useCompany = (
  id?: string,
  queryProp?: UseQueryOptions<Business>,
) => {
  const { data, ...rest } = useQuery<Business>(
    ['business', id],
    () => getCompanyById(id!),
    { ...queryProp, enabled: !!id },
  );
  return {
    company: data,
    ...rest,
  };
};

export const useCompanyByUserId = (
  id?: string,
  queryProps?: UseQueryOptions<Business | undefined>,
) => {
  const { isAuthenticated } = useAuth0();
  const token = AuthService.userToken();
  const { data, ...rest } = useQuery<Business | undefined, unknown>(
    ['company/user', 'userid', id],
    () =>
      getCompanyListByUserId(id).then((companies) => {
        if (companies.length > 0) {
          return companies[0];
        }
        return undefined;
      }),
    { ...queryProps, enabled: !!id && isAuthenticated && !!token },
  );

  return {
    company: data,
    ...rest,
  };
};

export const useCurrentCompany = (
  options?: UseQueryOptions<Business | undefined>,
) => {
  const { currentCompanyId } = useFormationsAuth();
  const { data, ...rest } = useQuery<Business | undefined>(
    ['business', currentCompanyId],
    () => getCompanyById(currentCompanyId!),
    { enabled: !!currentCompanyId, ...options },
  );
  return {
    currentCompany: data,
    ...rest,
  };
};

export const useCurrentBusinessOrganizationId = () => {
  const { currentCompany } = useCurrentCompany();
  return currentCompany?.organizationId || FormationsOrganization.Formations;
};

export interface UseUpdateCompanyVariables {
  id: string;
  data: any;
}

export const useUpdateCompany = (
  mutationProps?: UseMutationOptions<
    unknown,
    unknown,
    UseUpdateCompanyVariables
  >,
) =>
  useMutation(
    ({ id, data }: UseUpdateCompanyVariables) => updateCompanyById(id, data),
    mutationProps,
  );

export const useCreateCompany = (
  mutationProps?: UseMutationOptions<unknown, unknown, Partial<Business>>,
) => {
  const { mutate, mutateAsync, ...rest } = useMutation(
    (data: Partial<Business>) => createCompany(data),
    mutationProps,
  );
  return {
    createCompany: mutate,
    createCompanyAsync: mutateAsync,
    ...rest,
  };
};

export const useUpdateCompanyProgress = (
  businessId: string,
  mutationProps?: UseMutationOptions<
    unknown,
    unknown,
    { progress: ProgressTrackerGroupRequest[]; eventData?: any }
  >,
) => {
  const { mutate, mutateAsync, ...rest } = useMutation(
    (data: { progress: ProgressTrackerGroupRequest[]; eventData?: any }) =>
      updateCompanyProgressById(businessId, data),
    {
      ...mutationProps,
      onSuccess: () => {
        queryClient.invalidateQueries(['company', businessId]);
        queryClient.invalidateQueries(['progressTracker', businessId]);
        queryClient.invalidateQueries(['business', businessId]);
      },
    },
  );
  return {
    updateCompanyProgress: mutate,
    updateCompanyProgressAsync: mutateAsync,
    ...rest,
  };
};

export const useUpdateIncorporationStatus = (companyId: string) => {
  const { mutate, mutateAsync, ...rest } = useMutation(
    (data: UpdateIncorporationStatusSetting) =>
      updateIncorporationStatus(companyId, data),
  );
  return {
    updateIncorporationStatus: mutate,
    updateIncorporationStatusAsync: mutateAsync,
    ...rest,
  };
};

export const useInitIncorporationStatus = (companyId: string) => {
  const { mutate, mutateAsync, ...rest } = useMutation(() =>
    initIncorporationStatus(companyId),
  );
  return {
    initIncorporationStatus: mutate,
    initIncorporationStatusAsync: mutateAsync,
    ...rest,
  };
};

export type CoverLetterData = {
  id?: string;
  createdAt?: string;
  updatedAt?: string;
  businessId: string;
  organizationId: string;
  title: string;
  body: string;
  bankInfo: string;
};
interface UseUpdateCoverLetterVariables {
  businessId: string;
  data: CoverLetterData;
}
export const useUpdateCoverLetter = (
  mutationProps?: UseMutationOptions<
    unknown,
    unknown,
    UseUpdateCoverLetterVariables
  >,
) => {
  const { mutate, mutateAsync, ...rest } = useMutation(
    ({ businessId, data }: UseUpdateCoverLetterVariables) =>
      updateCompanyCoverLetter(businessId, data),
    mutationProps,
  );
  return {
    updateCoverLetter: mutate,
    updateCoverLetterAsync: mutateAsync,
    ...rest,
  };
};

export const useCurrentCoverLetter = () => {
  const { currentCompanyId } = useFormationsAuth();
  const { data, ...rest } = useQuery<CoverLetterData | null | undefined>(
    ['companyCoverLetter', currentCompanyId],
    () => getCompanyCoverLetter(currentCompanyId!),
    { enabled: !!currentCompanyId },
  );
  return {
    coverLetter: data,
    ...rest,
  };
};

export const useActivateBusiness = (businessId: string) => {
  const { mutate, mutateAsync, ...rest } = useMutation(() =>
    activateBusiness(businessId),
  );

  return {
    activateBusiness: mutate,
    activateBusinessAsync: mutateAsync,
    ...rest,
  };
};

export const useNotifyIncorporationStatus = (companyId: string) => {
  const { mutate, mutateAsync, ...rest } = useMutation(() =>
    notifyIncorporationStatus(companyId),
  );
  return {
    notifyIncorporationStatus: mutate,
    notifyIncorporationStatusAsync: mutateAsync,
    ...rest,
  };
};

interface UseCreateOwnerVariables {
  businessId?: string;
  data: OwnerRequestData;
}

export const useCreateOwner = (
  mutationProps?: UseMutationOptions<unknown, unknown, UseCreateOwnerVariables>,
) =>
  useMutation(
    ({ businessId, data }: UseCreateOwnerVariables) =>
      createOwner(businessId!, data),
    mutationProps,
  );

interface UseUpdateOwnerVariables {
  ownerId: string;
  businessId: string;
  data: Partial<OwnerRequestData>;
}

export const useUpdateOwner = (
  mutationProps?: UseMutationOptions<unknown, unknown, UseUpdateOwnerVariables>,
) =>
  useMutation(
    ({ ownerId, businessId, data }: UseUpdateOwnerVariables) =>
      updateOwner(ownerId, businessId, data),
    mutationProps,
  );

interface UseDeleteOwnerVariables {
  ownerId: string;
  businessId: string;
}

export const useDeleteOwner = (
  mutationProps?: UseMutationOptions<unknown, unknown, UseDeleteOwnerVariables>,
) =>
  useMutation(
    ({ ownerId, businessId }: UseDeleteOwnerVariables) =>
      deleteOwner(ownerId, businessId),
    mutationProps,
  );

export const useOwners = (businessId?: string) => {
  const { data, ...rest } = useQuery<Owner[] | null | undefined>(
    ['businessOwners', businessId],
    () => getOwners(businessId!),
    { enabled: !!businessId },
  );
  return {
    owners: data,
    ...rest,
  };
};

export const useOwner = (
  ownerId?: string,
  businessId?: string,
  viewSSN?: boolean,
) => {
  const { data, ...rest } = useQuery<Owner | null | undefined>(
    ['businessOwner', ownerId, businessId],
    () => getOwner(ownerId!, businessId!, viewSSN),
    { enabled: !!ownerId && !!businessId },
  );
  return {
    owner: data,
    ...rest,
  };
};

export const useCompanySetupState = (companyId?: string) => {
  const { data, ...rest } = useQuery<CompanySetupState | undefined>(
    ['companySetupState', companyId],
    () => getCompanySetupState(companyId!),
    { enabled: !!companyId },
  );
  return {
    companySetupState: data,
    ...rest,
  };
};

export const useUpdateCompanySetupState = (companyId?: string) => {
  const { mutate, mutateAsync, ...rest } = useMutation(
    (data: Partial<UpdateCompanySetupState>) => {
      if (!companyId) {
        return Promise.reject(new Error('Business ID is required'));
      }
      return setCompanySetupState(companyId, data);
    },
  );

  return {
    updateCompanySetupState: mutate,
    updateCompanySetupStateAsync: mutateAsync,
    ...rest,
  };
};

const stripCompanySetupState = (
  state: Partial<CompanySetupState>,
): Partial<CompanySetupState> => omit(state, ['id', 'createdAt', 'updatedAt']);

export const useUpdateCompanySetupStateAsync = () => {
  const { currentAccount } = useCurrentAccount();
  const { currentCompany } = useCurrentCompany();
  const { companySetupState } = useCompanySetupState(currentCompany?.id);
  const { updateCompanySetupStateAsync, ...rest } = useUpdateCompanySetupState(
    currentCompany?.id,
  );
  const mutateAsync = async (data: Partial<UpdateCompanySetupState>) => {
    if (companySetupState) {
      return updateCompanySetupStateAsync({
        ...stripCompanySetupState(companySetupState),
        ...data,
      });
    }
    return updateCompanySetupStateAsync({
      accountId: currentAccount?.id,
      businessId: currentCompany?.id,
      organizationId: currentCompany?.organizationId,
      ...data,
    });
  };
  return {
    updateCompanySetupStateAsync: mutateAsync,
    ...rest,
  };
};

export const useUpdateCompanySetupStateAsyncAdmin = (
  businessId: string,
  accountId: string,
) => {
  const { companySetupState } = useCompanySetupState(businessId);
  const { updateCompanySetupStateAsync, ...rest } =
    useUpdateCompanySetupState(businessId);
  const mutateAsync = async (data: Partial<UpdateCompanySetupState>) => {
    if (companySetupState) {
      return updateCompanySetupStateAsync({
        ...stripCompanySetupState(companySetupState),
        ...data,
      });
    }
    return updateCompanySetupStateAsync({
      accountId,
      businessId,
      ...data,
    });
  };
  return {
    updateCompanySetupStateAsync: mutateAsync,
    ...rest,
  };
};

export const useUpdateBusinessGoals = (
  companyId: string,
  options?: UseMutationOptions<unknown, unknown, BusinessGoalsRequest>,
) => {
  const { mutate, mutateAsync, ...rest } = useMutation(
    (data: BusinessGoalsRequest) => {
      if (!companyId) {
        return Promise.reject(new Error('Business ID is required'));
      }
      return updateBusinessGoals(companyId, data);
    },
    options,
  );
  return {
    updateBusinessGoals: mutate,
    updateBusinessGoalsAsync: mutateAsync,
    ...rest,
  };
};

export const useBusinessProjectedGrossIncome = (
  businessId: string,
  year: number,
  options?: UseQueryOptions<BusinessProjectedGrossIncome>,
) => {
  const { data, ...rest } = useQuery<BusinessProjectedGrossIncome>(
    ['businessProjectedGrossIncome', businessId],
    () => getBusinessProjectedGrossIncome(businessId, year),
    { enabled: !!businessId, ...options },
  );
  return {
    businessProjectedGrossIncome: data,
    ...rest,
  };
};

export const useProjectedBusinessExpense = (
  businessId: string,
  year: number,
  options?: UseQueryOptions<BusinessProjectedBusinessExpense>,
) => {
  const { data, ...rest } = useQuery<BusinessProjectedBusinessExpense>(
    ['projectedBusinessExpense', businessId],
    () => getProjectedBusinessExpense(businessId, year),
    { enabled: !!businessId, ...options },
  );
  return {
    projectedBusinessExpense: data,
    ...rest,
  };
};
